// path: frontend/src/components/Gantt/index.jsx
/**
 * Диаграмма Ганта.
 *
 * После PR 2b-2b computeScope/taskVisible убраны: useScopeDb отдаёт
 * /tasks уже scope-фильтрованным (task.repo.ts::applyScope на сервере,
 * showAll=false по умолчанию). UI-фильтр по t.isActive (серверное поле
 * TaskDto) оставлен — это отображение архивных закрытых задач старше
 * 3 месяцев.
 *
 * Drag разрешён, если task.permissions.canEdit=true (сервер отдаёт
 * per-task в TaskDto). Окончательная проверка — TaskService.update
 * (403, если canEditTaskFields=false).
 */
import {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  memo,
} from "react";
import { Ic } from "../Icons";
import { ICONS } from "../iconsData";
import { Select } from "../Select";
import { SearchBox } from "../SearchBox";
import { useToast } from "../../context/ToastContext";
import { useConfirm } from "../../context/ConfirmContext";
import { useFilters } from "../../hooks/useFilters";
import { useFilterOptions } from "../../hooks/useFilterOptions";
import { useScopeDb } from "../../hooks/useDb";
import { useDataHelpers } from "../../hooks/useDataHelpers";
import { useTaskActions } from "../../hooks/useTaskActions";
import { useUpdateTaskMutation } from "../../api/queries/tasks";
import { useProductionCalendarQuery } from "../../api/queries/production-calendar";
import { TASK_STATUSES } from "../../utils/constants";
import { iso, parseISO, addDays, TODAY } from "../../utils/date";
import {
  toProjectOption,
  toEmployeeOption,
  toStatusEntryOption,
} from "../../utils/selectOptions";
import { formatPeriodLabel } from "../../utils/periodLabel";
import {
  normalizeAnchor,
  computeTaskIndices,
  UNKNOWN_PROJECT,
  UNKNOWN_PROJECT_ID,
} from "./geometry";
import { buildRowOrder } from "./tree";
import { mapsEqual } from "./dependencyArrows";
import { useGanttDrag } from "./useGanttDrag";
import { GanttProjectGroup } from "./GanttProjectGroup";
import { GanttDependencyArrows } from "./GanttDependencyArrows";

const INITIAL_FILTERS = Object.freeze({
  projectId: "all",
  assigneeId: "all",
  status: "all",
  query: "",
});

const WORK_HOURS_BY_STATUS = Object.freeze({
  workday: 8,
  shortday: 7,
  weekend: 0,
  holiday: 0,
});

function GanttMonth({ label, days }) {
  const ref = useRef(null);
  useLayoutEffect(() => {
    ref.current?.style.setProperty("--month-days", String(days));
  }, [days]);
  return (
    <div ref={ref} className="gantt-month">
      {label}
    </div>
  );
}

function Gantt({
  ur,
  openTask,
  openProject,
  openCopyTask,
  openTemplateFromTask,
}) {
  const db = useScopeDb();
  const { tasks, projects, employees } = db;
  const { getTaskSpent, vacOverlap } = useDataHelpers(db);
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  const { filters, setFilter } = useFilters(INITIAL_FILTERS);
  const { projectId, assigneeId, status, query } = filters;

  const updateTaskMutation = useUpdateTaskMutation();
  const { data: calendarData } = useProductionCalendarQuery();
  const { handleMove: handleMoveTask, handleDelete: handleDeleteTask } =
    useTaskActions({ ur, db, toast: showToast });

  const employeesById = useMemo(
    () => new Map(employees.map((e) => [e.id, e])),
    [employees],
  );
  const projectsById = useMemo(
    () => new Map(projects.map((p) => [p.id, p])),
    [projects],
  );

  const [zoomLevel, setZoomLevel] = useState(1);
  const DW = Math.round(34 * zoomLevel);
  const [expandedTasks, setExpandedTasks] = useState(() => new Set());
  const [mode, setMode] = useState("month");
  const [anchor, setAnchor] = useState(() => {
    const now = new Date();
    return iso(new Date(now.getFullYear(), now.getMonth(), 1));
  });
  const [cornerWidth, setCornerWidth] = useState(500);
  const [fullscreen, setFullscreen] = useState(false);
  const scrollRef = useRef(null);
  const resizerRef = useRef(null);
  const pendingScrollRef = useRef(false);
  const ganttRef = useRef(null);

  const statusOptions = useMemo(
    () =>
      Object.entries(TASK_STATUSES).filter(
        ([k]) => k !== "closed" && k !== "cancelled" && k !== "draft",
      ),
    [],
  );

  useEffect(() => {
    const allowed = new Set(["all", ...statusOptions.map(([k]) => k)]);
    if (!allowed.has(status)) setFilter("status", "all");
  }, [status, statusOptions, setFilter]);

  const changeMode = useCallback((newMode) => {
    setMode(newMode);
    setAnchor((prev) => normalizeAnchor(prev, newMode));
  }, []);

  useEffect(() => {
    if (!fullscreen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setFullscreen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [fullscreen]);

  const onToggleTask = useCallback((id) => {
    setExpandedTasks((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleResizerMouseDown = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      const el = resizerRef.current;
      if (el) el.classList.add("dragging");
      const startX = e.clientX;
      const startWidth = cornerWidth;
      let rafId = null;
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";

      const handleMove = (ev) => {
        if (rafId) return;
        rafId = requestAnimationFrame(() => {
          rafId = null;
          setCornerWidth(
            Math.max(150, Math.min(500, startWidth + ev.clientX - startX)),
          );
        });
      };
      const cleanup = () => {
        if (el) el.classList.remove("dragging");
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
        if (rafId) cancelAnimationFrame(rafId);
        document.removeEventListener("mousemove", handleMove);
        document.removeEventListener("mouseup", cleanup);
        document.removeEventListener("keydown", onKey);
      };
      const onKey = (ev) => {
        if (ev.key !== "Escape") return;
        setCornerWidth(startWidth);
        cleanup();
      };
      document.addEventListener("mousemove", handleMove);
      document.addEventListener("mouseup", cleanup);
      document.addEventListener("keydown", onKey);
    },
    [cornerWidth],
  );

  const getDaysInRange = useCallback((anchorDate, modeArg) => {
    const start = parseISO(anchorDate);
    let end;
    if (modeArg === "quarter")
      end = new Date(start.getFullYear(), start.getMonth() + 3, 1);
    else if (modeArg === "year") end = new Date(start.getFullYear() + 1, 0, 1);
    else end = new Date(start.getFullYear(), start.getMonth() + 1, 1);
    const days = [];
    for (const d = new Date(start); d < end; d.setDate(d.getDate() + 1))
      days.push(iso(d));
    return days;
  }, []);

  const shift = useCallback(
    (dir) => {
      const d = parseISO(anchor);
      if (mode === "month") d.setMonth(d.getMonth() + dir);
      else if (mode === "quarter") d.setMonth(d.getMonth() + dir * 3);
      else d.setFullYear(d.getFullYear() + dir);
      setAnchor(normalizeAnchor(iso(d), mode));
    },
    [anchor, mode],
  );

  const baseTasks = useMemo(() => {
    return tasks
      .filter((t) => {
        if (!t.isActive) return false;
        if (!t.start) return false;
        if (!t.deadline) return false;
        return true;
      })
      .map((t) => ({
        ...t,
        start: t.start ? t.start.slice(0, 10) : null,
        deadline: t.deadline ? t.deadline.slice(0, 10) : null,
      }));
  }, [tasks]);

  const allTasks = useMemo(() => {
    let list = baseTasks;
    if (projectId !== "all")
      list = list.filter((t) => t.projectId === projectId);
    if (assigneeId !== "all")
      list = list.filter((t) => t.assigneeId === assigneeId);
    if (status !== "all") list = list.filter((t) => t.status === status);
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      list = list.filter((t) => (t.title || "").toLowerCase().includes(q));
    }
    return list;
  }, [baseTasks, projectId, assigneeId, status, query]);

  const projectOptions = useMemo(() => {
    const ids = new Set(baseTasks.map((t) => t.projectId).filter(Boolean));
    return projects.filter((p) => ids.has(p.id));
  }, [baseTasks, projects]);

  const tasksForAssignee = useMemo(() => {
    let list = baseTasks;
    if (projectId !== "all")
      list = list.filter((t) => t.projectId === projectId);
    return list;
  }, [baseTasks, projectId]);

  const assigneeOptions = useMemo(() => {
    const ids = new Set(
      tasksForAssignee.map((t) => t.assigneeId).filter(Boolean),
    );
    return employees.filter((e) => ids.has(e.id));
  }, [tasksForAssignee, employees]);

  const projectSelectOptions = useFilterOptions(
    projectOptions,
    "Все проекты",
    toProjectOption,
  );
  const assigneeSelectOptions = useFilterOptions(
    assigneeOptions,
    "Все исполнители",
    toEmployeeOption,
  );
  const statusSelectOptions = useFilterOptions(
    statusOptions,
    "Все статусы",
    toStatusEntryOption,
  );

  const days = useMemo(
    () => getDaysInRange(anchor, mode),
    [anchor, mode, getDaysInRange],
  );
  const daysLength = days.length;
  const dayIndexByIso = useMemo(() => {
    const m = new Map();
    days.forEach((d, i) => m.set(d, i));
    return m;
  }, [days]);
  const todayIdx = dayIndexByIso.get(TODAY) ?? -1;

  const calendarSets = useMemo(() => {
    const holidays = new Set();
    const shortDays = new Set();
    for (const yearDto of calendarData ?? []) {
      if (!yearDto) continue;
      if (Array.isArray(yearDto.holidays)) {
        for (const d of yearDto.holidays) holidays.add(d);
      }
      if (Array.isArray(yearDto.shortDays)) {
        for (const d of yearDto.shortDays) shortDays.add(d);
      }
    }
    return { holidays, shortDays };
  }, [calendarData]);

  const statusForIso = useCallback(
    (isoDate) => {
      if (calendarSets.holidays.has(isoDate)) return "holiday";
      if (calendarSets.shortDays.has(isoDate)) return "shortday";
      const dow = parseISO(isoDate).getDay();
      return dow === 0 || dow === 6 ? "weekend" : "workday";
    },
    [calendarSets],
  );

  const dayStatusByIso = useMemo(() => {
    const map = new Map();
    for (const d of days) map.set(d, statusForIso(d));
    return map;
  }, [days, statusForIso]);

  const months = useMemo(() => {
    const result = [];
    days.forEach((day, i) => {
      const d = parseISO(day);
      const lbl = `${["Январь", "Февраль", "Март", "Апрель", "Май", "Июнь", "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь"][d.getMonth()]} ${d.getFullYear()}`;
      if (!result.length || result[result.length - 1].label !== lbl)
        result.push({ label: lbl, from: i, to: i });
      else result[result.length - 1].to = i;
    });
    return result;
  }, [days]);

  const viewStart = days[0];
  const viewEnd = days[daysLength - 1];
  const width = daysLength * DW;
  const totalWidth = width + cornerWidth;

  const groupsWithRows = useMemo(() => {
    const groups = new Map();
    allTasks.forEach((t) => {
      const key = t.projectId || UNKNOWN_PROJECT_ID;
      let g = groups.get(key);
      if (!g) {
        g = {
          project: projectsById.get(t.projectId) || UNKNOWN_PROJECT,
          tasks: [],
        };
        groups.set(key, g);
      }
      g.tasks.push(t);
    });
    return Array.from(groups.values())
      .map((g) => {
        const order = buildRowOrder(g.tasks, expandedTasks);
        const visibleRows = [];
        for (const row of order) {
          const indices = computeTaskIndices(
            row.node,
            dayIndexByIso,
            daysLength,
            viewStart,
            viewEnd,
          );
          if (!indices) continue;
          visibleRows.push({ ...row, indices });
        }
        return { project: g.project, visibleRows };
      })
      .filter((g) => g.visibleRows.length > 0);
  }, [
    allTasks,
    projectsById,
    expandedTasks,
    dayIndexByIso,
    daysLength,
    viewStart,
    viewEnd,
  ]);

  const taskIndexById = useMemo(() => {
    const map = new Map();
    for (const group of groupsWithRows) {
      for (const row of group.visibleRows)
        map.set(row.id, { row, group: group.project });
    }
    return map;
  }, [groupsWithRows]);

  const bodyRef = useRef(null);
  const rowElsRef = useRef(new Map());
  const [rowMetrics, setRowMetrics] = useState(() => ({
    yById: new Map(),
    height: 0,
  }));

  const registerRowEl = useCallback((id, el) => {
    if (el) rowElsRef.current.set(id, el);
    else rowElsRef.current.delete(id);
  }, []);

  useLayoutEffect(() => {
    const body = bodyRef.current;
    if (!body) return undefined;

    const measure = () => {
      const bodyRect = body.getBoundingClientRect();
      const yById = new Map();
      let maxBottom = 0;
      for (const [id, el] of rowElsRef.current) {
        const r = el.getBoundingClientRect();
        const top = r.top - bodyRect.top;
        yById.set(id, top + r.height / 2);
        if (top + r.height > maxBottom) maxBottom = top + r.height;
      }
      setRowMetrics((prev) =>
        prev.height === maxBottom && mapsEqual(prev.yById, yById)
          ? prev
          : { yById, height: maxBottom },
      );
    };

    measure();

    const ro = new ResizeObserver(measure);
    ro.observe(body);
    return () => ro.disconnect();
  }, []);

  useLayoutEffect(() => {
    const el = ganttRef.current;
    if (!el) return;
    el.style.setProperty("--gantt-width", `${totalWidth}px`);
    el.style.setProperty("--corner-width", `${cornerWidth}px`);
    el.style.setProperty("--gantt-days-width", `${width}px`);
    el.style.setProperty("--day-width", `${DW}px`);
    el.style.setProperty(
      "--today-left",
      todayIdx >= 0 ? `${todayIdx * DW + DW / 2}px` : "",
    );
    el.style.setProperty("--arrows-height", `${rowMetrics.height}px`);
  }, [totalWidth, cornerWidth, width, DW, todayIdx, rowMetrics.height]);

  const canEdit = useCallback((task) => Boolean(task.permissions?.canEdit), []);

  const handleDragCommit = useCallback(
    async (task, mode, daysDelta) => {
      const origStart = task.start;
      const origDeadline = task.deadline;
      const shiftDate = (s, d) => (s ? iso(addDays(parseISO(s), d)) : s);

      let nextStart = origStart;
      let nextDeadline = origDeadline;
      if (mode === "move") {
        nextStart = shiftDate(origStart, daysDelta);
        nextDeadline = shiftDate(origDeadline, daysDelta);
      } else if (mode === "resize-left") {
        nextStart = shiftDate(origStart, daysDelta);
        if (nextStart > nextDeadline) nextStart = nextDeadline;
      } else if (mode === "resize-right") {
        nextDeadline = shiftDate(origDeadline, daysDelta);
        if (nextDeadline < nextStart) nextDeadline = nextStart;
      }

      if (nextStart === origStart && nextDeadline === origDeadline) return;

      // Проверка на нерабочие дни (выходные или праздники)
      const startStatus = nextStart ? statusForIso(nextStart) : null;
      const endStatus = nextDeadline ? statusForIso(nextDeadline) : null;

      const isStartNonWorking =
        startStatus === "weekend" || startStatus === "holiday";
      const isEndNonWorking =
        endStatus === "weekend" || endStatus === "holiday";

      if (isStartNonWorking || isEndNonWorking) {
        const ok = await confirm({
          title: "Нерабочий день",
          message: `Задача попадает на нерабочий день. Вы уверены, что хотите сохранить изменения?`,
          confirmLabel: "Сохранить",
          danger: false,
        });
        if (!ok) return; // Если пользователь отменил — задача останется на старом месте
      }

      updateTaskMutation
        .mutateAsync({
          id: task.id,
          body: { startDate: nextStart, deadline: nextDeadline },
          needsConfirmation: true,
        })
        .catch((err) => {
          showToast(err.message || "Не удалось изменить срок задачи", "error");
        });
    },
    [updateTaskMutation, showToast, statusForIso, confirm],
  );

  const hoursBetween = useCallback(
    (startISO, endISO) => {
      if (!startISO || !endISO) return 0;
      const d = parseISO(startISO);
      const end = parseISO(endISO);
      if (d.getTime() > end.getTime()) return 0;
      let total = 0;
      while (d.getTime() <= end.getTime()) {
        total += WORK_HOURS_BY_STATUS[statusForIso(iso(d))];
        d.setDate(d.getDate() + 1);
      }
      return total;
    },
    [statusForIso],
  );

  const getDragBounds = useCallback(
    (task, dragMode) => {
      // Для перемещения (move) разрешаем двигать задачу куда угодно.
      // Проверка на нерабочие дни и валидацию часов будет при сохранении.
      if (dragMode === "move") return null;

      // Для изменения размера (resize) оставляем логику ограничения,
      // чтобы нельзя было сжать задачу ниже плановых часов.
      const planned = task.plannedHours || 0;
      if (planned <= 0) return null;
      const { start, deadline } = task;
      if (!start || !deadline || start > deadline) return null;

      if (hoursBetween(start, deadline) < planned) return { min: 0, max: 0 };

      const dStart = parseISO(start);
      const dDeadline = parseISO(deadline);
      const spanDays = Math.max(0, Math.round((dDeadline - dStart) / 86400000));

      if (dragMode === "resize-left") {
        let maxDelta = 0;
        for (let delta = 1; delta <= spanDays; delta++) {
          const nd = new Date(dStart);
          nd.setDate(nd.getDate() + delta);
          if (hoursBetween(iso(nd), deadline) >= planned) maxDelta = delta;
          else break;
        }
        return { min: -Infinity, max: maxDelta };
      }

      if (dragMode === "resize-right") {
        let minDelta = 0;
        for (let delta = -1; delta >= -spanDays; delta--) {
          const nd = new Date(dDeadline);
          nd.setDate(nd.getDate() + delta);
          if (hoursBetween(start, iso(nd)) >= planned) minDelta = delta;
          else break;
        }
        return { min: minDelta, max: Infinity };
      }

      return null;
    },
    [hoursBetween],
  );

  const { onBarMouseDown, detectMode } = useGanttDrag({
    enabled: true,
    DW,
    onCommit: handleDragCommit,
    getBounds: getDragBounds,
  });

  const handleCollapseAll = useCallback(() => {
    setExpandedTasks(new Set());
  }, []);
  const handleExpandAll = useCallback(() => {
    const parentIds = new Set();
    allTasks.forEach((t) => {
      if (t.parentTaskId) parentIds.add(t.parentTaskId);
    });
    setExpandedTasks(parentIds);
  }, [allTasks]);

  const scrollToToday = useCallback(() => {
    const idx = days.indexOf(TODAY);
    if (idx !== -1) {
      const el = scrollRef.current;
      if (!el) return;
      const target = cornerWidth + idx * DW - el.clientWidth / 2 + DW / 2;
      el.scrollTo({ left: Math.max(0, target), behavior: "smooth" });
      return;
    }
    pendingScrollRef.current = true;
    setAnchor(normalizeAnchor(iso(new Date()), mode));
  }, [days, DW, cornerWidth, mode]);

  useEffect(() => {
    if (!pendingScrollRef.current) return;
    pendingScrollRef.current = false;
    const idx = days.indexOf(TODAY);
    if (idx === -1) return;
    const el = scrollRef.current;
    if (!el) return;
    const target = cornerWidth + idx * DW - el.clientWidth / 2 + DW / 2;
    el.scrollTo({ left: Math.max(0, target), behavior: "smooth" });
  }, [days, DW, cornerWidth]);

  const dayCellClass = (isoDate) => {
    const st = dayStatusByIso.get(isoDate);
    if (st === "holiday") return "gcell holiday";
    if (st === "shortday") return "gcell shortday";
    if (st === "weekend") return "gcell wk";
    return "gcell";
  };
  const dayHeadClass = (isoDate) => {
    const st = dayStatusByIso.get(isoDate);
    const base = "gday";
    const isToday = isoDate === TODAY ? " td" : "";
    if (st === "holiday") return `${base} holiday${isToday}`;
    if (st === "shortday") return `${base} shortday${isToday}`;
    if (st === "weekend") return `${base} wk${isToday}`;
    return `${base}${isToday}`;
  };

  return (
    <div className={`gantt-panel${fullscreen ? " fullscreen" : ""}`}>
      <div className="gantt-filter-bar">
        <SearchBox
          value={query}
          onChange={(v) => setFilter("query", v)}
          placeholder="Поиск по названию…"
          className="gantt-search"
        />
        <Select
          className="gantt-filter-select"
          value={projectId}
          onChange={(v) => setFilter("projectId", v)}
          options={projectSelectOptions}
        />
        <Select
          className="gantt-filter-select"
          value={assigneeId}
          onChange={(v) => setFilter("assigneeId", v)}
          options={assigneeSelectOptions}
        />
        <Select
          className="gantt-filter-select"
          value={status}
          onChange={(v) => setFilter("status", v)}
          options={statusSelectOptions}
        />
        <div className="gantt-zoom">
          <button
            type="button"
            className="icon-btn"
            aria-label="Уменьшить масштаб"
            onClick={() => setZoomLevel(Math.max(0.5, zoomLevel - 0.25))}
          >
            −
          </button>
          <span className="zoom-value">{Math.round(zoomLevel * 100)}%</span>
          <button
            type="button"
            className="icon-btn"
            aria-label="Увеличить масштаб"
            onClick={() => setZoomLevel(Math.min(2, zoomLevel + 0.25))}
          >
            +
          </button>
        </div>
        <button
          type="button"
          className="btn ghost sm"
          onClick={handleCollapseAll}
        >
          Свернуть всё
        </button>
        <button
          type="button"
          className="btn ghost sm"
          onClick={handleExpandAll}
        >
          Развернуть всё
        </button>
        <button
          type="button"
          className="btn ghost sm"
          onClick={() => setFullscreen((v) => !v)}
          title={
            fullscreen
              ? "Выйти из полноэкранного режима (Esc)"
              : "На весь экран"
          }
        >
          {fullscreen ? "✕ Выйти" : "⛶ Fullscreen"}
        </button>
      </div>

      <div className="gantt-period-head">
        <div className="gantt-period-nav">
          <button
            type="button"
            className="icon-btn"
            aria-label="Предыдущий период"
            onClick={() => shift(-1)}
          >
            <Ic d={ICONS.left} size={16} />
          </button>
          <div className="gantt-period-title">
            {formatPeriodLabel(mode, anchor, viewStart, viewEnd)}
          </div>
          <button
            type="button"
            className="icon-btn"
            aria-label="Следующий период"
            onClick={() => shift(1)}
          >
            <Ic d={ICONS.right} size={16} />
          </button>
          <button
            type="button"
            className="btn ghost sm"
            onClick={scrollToToday}
            title="Перейти к текущей дате"
          >
            Сегодня
          </button>
        </div>
        <div className="gantt-period-right">
          <div className="seg">
            {[
              ["month", "Месяц"],
              ["quarter", "Квартал"],
              ["year", "Год"],
            ].map(([m, l]) => (
              <button
                type="button"
                key={m}
                className={`seg-btn${mode === m ? " on" : ""}`}
                onClick={() => changeMode(m)}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="gantt-scroll" ref={scrollRef}>
        {groupsWithRows.length === 0 ? (
          <div className="empty-note p-4">
            Нет доступных задач в выбранном периоде
          </div>
        ) : (
          <div className="gantt" ref={ganttRef}>
            <div className="gantt-top">
              <div className="gantt-corner">
                Проект / задача
                <div
                  ref={resizerRef}
                  className="gantt-resizer"
                  onMouseDown={handleResizerMouseDown}
                />
              </div>
              <div className="gantt-axis">
                <div className="gantt-months">
                  {months.map((m, i) => (
                    <GanttMonth
                      key={i}
                      label={m.label}
                      days={m.to - m.from + 1}
                    />
                  ))}
                </div>
                <div className="gantt-days">
                  {days.map((d) => {
                    const dt = parseISO(d);
                    return (
                      <div key={d} className={dayHeadClass(d)}>
                        {dt.getDate()}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="gantt-body" ref={bodyRef}>
              <div className="gantt-grid">
                {days.map((d) => (
                  <div key={d} className={dayCellClass(d)} />
                ))}
                {todayIdx >= 0 && <div className="gtoday" />}
              </div>

              {groupsWithRows.map((g) => (
                <GanttProjectGroup
                  key={g.project.id}
                  project={g.project}
                  visibleRows={g.visibleRows}
                  DW={DW}
                  employeesById={employeesById}
                  projectsById={projectsById}
                  openTask={openTask}
                  openProject={openProject}
                  getTaskSpent={getTaskSpent}
                  vacOverlap={vacOverlap}
                  expandedTasks={expandedTasks}
                  onToggleTask={onToggleTask}
                  onDragStart={onBarMouseDown}
                  detectDragMode={detectMode}
                  db={db}
                  user={ur}
                  canEdit={canEdit}
                  onMove={handleMoveTask}
                  onDelete={handleDeleteTask}
                  onCopy={openCopyTask}
                  onMakeTemplate={openTemplateFromTask}
                  registerRowEl={registerRowEl}
                />
              ))}

              <GanttDependencyArrows
                rows={groupsWithRows}
                indexById={taskIndexById}
                DW={DW}
                yById={rowMetrics.yById}
                height={rowMetrics.height}
              />
            </div>
          </div>
        )}
      </div>

      <div className="gantt-legend p-2 border-t flex flex-wrap gap-4 items-center">
        <span className="legend-item">🏖 исполнитель в отпуске</span>
        <span className="legend-item">Заполнение полосы - факт / план</span>
      </div>
    </div>
  );
}

export default memo(Gantt);
